import { useState, useEffect, useRef } from 'react'
import { AlertCircle, AlertTriangle, CheckCircle2, X } from 'lucide-react'
import { apiHealth } from '../api/core'

/**
 * NetworkToast — global toast notification for online/offline transitions.
 *
 * Shows a slide-in toast when:
 *   - Connection is lost → red "Нет соединения"
 *   - Connection is restored → green "Подключение восстановлено"
 *
 * Auto-hides after 4 seconds. Always visible while offline.
 */
export default function NetworkToast() {
    const [isOnline, setIsOnline] = useState(navigator.onLine)
    const [apiDown, setApiDown] = useState(false)
    const [visible, setVisible] = useState(false)
    const [wasOffline, setWasOffline] = useState(false)
    const timerRef = useRef(null)
    const initialRef = useRef(true) // skip first render

    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true)
            if (wasOffline) {
                setVisible(true)
                // Auto-hide after 4s
                if (timerRef.current) clearTimeout(timerRef.current)
                timerRef.current = setTimeout(() => setVisible(false), 4000)
            }
        }

        const handleOffline = () => {
            setIsOnline(false)
            setWasOffline(true)
            setVisible(true)
            // Don't auto-hide while offline — keep the banner
            if (timerRef.current) clearTimeout(timerRef.current)
        }

        window.addEventListener('online', handleOnline)
        window.addEventListener('offline', handleOffline)

        // Skip initial render
        if (initialRef.current) {
            initialRef.current = false
            if (!navigator.onLine) {
                setWasOffline(true)
                setVisible(true)
            }
        }

        return () => {
            window.removeEventListener('online', handleOnline)
            window.removeEventListener('offline', handleOffline)
            if (timerRef.current) clearTimeout(timerRef.current)
        }
    }, [wasOffline])

    // Backend reachability poll: navigator.onLine === true does not mean
    // the API answers (Railway 502 / cold-start / DNS issues). Poll /health
    // and surface a distinct banner so users do not assume "all is fine"
    // when only the local network link is up.
    //
    // Hysteresis: один промах — не инцидент. Railway cold-start, transient 502,
    // блокировщики расширений дают регулярные одиночные промахи. Показываем
    // баннер только после 2 промахов подряд; убираем по первому успеху.
    // Иначе баннер флапает раз в 30с и создаёт ощущение "связь прыгает".
    useEffect(() => {
        let cancelled = false
        let missStreak = 0
        const check = async () => {
            if (!navigator.onLine) return
            const resp = await apiHealth()
            if (cancelled) return
            const ok = !!resp && (resp.status === 'ok' || resp.status === 'healthy' || resp.ok === true)
            if (ok) {
                missStreak = 0
                setApiDown(prev => {
                    if (prev) setVisible(true)
                    return false
                })
            } else {
                missStreak += 1
                if (missStreak >= 2) {
                    setApiDown(prev => {
                        if (!prev) setVisible(true)
                        return true
                    })
                }
            }
        }
        check()
        const id = setInterval(check, 30000)
        return () => { cancelled = true; clearInterval(id) }
    }, [])

    const showApiDown = isOnline && apiDown
    const showOffline = !isOnline
    if (!visible && !showApiDown && !showOffline) return null

    // Apple HIG hex — var(--color-warning) var(--color-warning), var(--color-success) var(--color-success),
    // var(--color-danger) var(--color-danger). Borders derived from actual DS tokens via rgba.
    // Background — непрозрачный (§17): color-mix с var(--color-bg-elevated), не transparent.
    const config = showApiDown
        ? {
            Icon: AlertTriangle,
            text: 'Сервер недоступен',
            subtext: 'Backend не отвечает. Изменения сохранятся и отправятся, когда он вернётся.',
            bg: 'color-mix(in srgb, var(--color-warning) 10%, var(--color-bg-elevated))',
            borderColor: 'color-mix(in srgb, var(--color-warning) 35%, var(--color-border))',
            iconColor: 'var(--color-warning)',
            titleColor: 'var(--color-warning)',
            glow: 'var(--shadow-md)',
            pulsing: true,
        }
        : isOnline
        ? {
            Icon: CheckCircle2,
            text: 'Подключение восстановлено',
            subtext: 'Данные синхронизируются...',
            bg: 'color-mix(in srgb, var(--color-success) 10%, var(--color-bg-elevated))',
            borderColor: 'color-mix(in srgb, var(--color-success) 35%, var(--color-border))',
            iconColor: 'var(--color-success)',
            titleColor: 'var(--color-success)',
            glow: 'var(--shadow-md)',
            pulsing: false,
        }
        : {
            Icon: AlertCircle,
            text: 'Нет соединения',
            subtext: 'Данные доступны из кэша. Изменения сохранятся при подключении.',
            bg: 'color-mix(in srgb, var(--color-danger) 10%, var(--color-bg-elevated))',
            borderColor: 'color-mix(in srgb, var(--color-danger) 35%, var(--color-border))',
            iconColor: 'var(--color-danger)',
            titleColor: 'var(--color-danger)',
            glow: 'var(--shadow-md)',
            pulsing: true,
        }

    const { Icon } = config

    return (
        <>
            <style>{`
                @keyframes network-toast-in {
                    from { transform: translate(-50%, -100%); opacity: 0; }
                    to { transform: translate(-50%, 0); opacity: 1; }
                }
                @keyframes network-toast-pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.65; }
                }
            `}</style>
            <div
                id="network-toast"
                role="alert"
                aria-live="assertive"
                onClick={() => isOnline && !showApiDown && setVisible(false)}
                style={{
                    position: 'fixed',
                    top: 12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10000,
                    padding: '10px 16px',
                    borderRadius: 'var(--radius-md)',
                    // Непрозрачный surface (§17): color-mix с bg-elevated, не с transparent
                    background: config.bg,
                    border: `1px solid ${config.borderColor}`,
                    boxShadow: config.glow,
                    animation: 'network-toast-in 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: isOnline && !showApiDown ? 'pointer' : 'default',
                    maxWidth: 'min(420px, calc(100vw - 24px))',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                }}
            >
                {/* Lucide icon вместо цветного символа '●' */}
                <span
                    aria-hidden="true"
                    style={{
                        color: config.iconColor,
                        flexShrink: 0,
                        display: 'inline-flex',
                        animation: config.pulsing ? 'network-toast-pulse 2s ease-in-out infinite' : 'none',
                    }}
                >
                    <Icon size={18} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Заголовок: тон-цвет, читается на любом фоне */}
                    <div
                        style={{
                            color: config.titleColor,
                            fontWeight: 700,
                            fontSize: '0.82rem',
                            lineHeight: 1.3,
                        }}
                    >
                        {config.text}
                    </div>
                    {/* Subtext: --color-text-secondary (#48484a / dark:#ebedf0) — контраст ≥4.5:1 */}
                    <div
                        style={{
                            color: 'var(--color-text-secondary)',
                            fontSize: '0.72rem',
                            lineHeight: 1.4,
                            marginTop: 2,
                        }}
                    >
                        {config.subtext}
                    </div>
                </div>
                {isOnline && !showApiDown && (
                    <button
                        type="button"
                        aria-label="Закрыть"
                        onClick={(e) => { e.stopPropagation(); setVisible(false) }}
                        style={{
                            flexShrink: 0,
                            background: 'transparent',
                            border: 'none',
                            padding: 4,
                            cursor: 'pointer',
                            color: 'var(--color-text-muted)',
                            display: 'inline-flex',
                            borderRadius: 'var(--radius-sm)',
                        }}
                    >
                        <X size={14} />
                    </button>
                )}
            </div>
        </>
    )
}
